import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useSession } from "../../auth/session"
import { Library, Sku, SkuVersionStatus, skuVersionStatusLabels } from "../../api/models"
import { useLibrary } from "../../redux/librarySlice"
import { FetchableStateStatus } from "../../redux/fetchableState"
import Spinner from "../widgets/Spinner"
import CardImage from "../widgets/CardImage"
import SkuName from "../widgets/SkuName"
import { Platform, platformLabels, userPlatform } from "../widgets/platform"
import AllVersionsDialog from "../AllVersionsDialog"
import { entryExpired } from "./Library"

// The download-first account view: one tile per DISTINCT sku the user holds
// an unexpired entitlement to (the Licenses page has one card per
// entitlement, so a sku bought twice — or a bundle plus an upgrade — repeats
// there). Nothing here but what installing needs: logo, screenshot, version
// and the installer links, so a grid of tiles rather than a column of
// rows; ownership details stay on Licenses. Built from
// the same GET /me/library payload — `skus` there is already one per sku,
// with the user's test build resolved.

// One downloadable version of a sku: the latest release, or the user's
// test build (status set)
interface InstallerRow {
  key: string
  version: string
  status?: SkuVersionStatus
  urls: Record<Platform, string | undefined>
}

// "Get v2.1.0 for macOS" — a real button that navigates to the installer
// url (the browser downloads it, as following the link would) for the
// visitor's platform (both platforms when it can't be detected); the other
// platform and older releases live in the All versions dialog
function DownloadButtons({ row }: { row: InstallerRow }) {
  const platforms: Platform[] = userPlatform !== null ? [userPlatform] : ["mac", "windows"]
  const buttons = platforms
    .filter(platform => row.urls[platform])
    .map(platform => (
      <button key={platform} type="button" className="btn-download" onClick={() => window.location.assign(row.urls[platform]!)}>
        Get v{row.version} for {platformLabels[platform]}
      </button>
    ))
  // the detected platform has no build of this version (e.g. a mac-only
  // beta) — say so rather than leave the row empty; All versions still
  // offers whatever exists
  if (buttons.length === 0)
    return <span className="library-muted">v{row.version} is not available for {platformLabels[userPlatform ?? "mac"]}</span>
  return <>{buttons}</>
}

// One sku's installer rows: the latest release (when an entitlement covers
// a released version) and the user's test build (when they hold one),
// each a button naming the version it downloads, then the All versions
// link (the dialog lists every version, so one link serves both rows).
function InstallerRows({ sku, hasReleasedEntry, accessToken }: { sku: Sku, hasReleasedEntry: boolean, accessToken: string }) {
  const [versionsOpen, setVersionsOpen] = useState(false)
  const rows: InstallerRow[] = []
  if (hasReleasedEntry && sku.latestVersion)
    rows.push({ key: "released", version: sku.latestVersion, urls: { mac: sku.downloadMacUrl, windows: sku.downloadWinUrl } })
  if (sku.testVersionId && sku.testVersion)
    rows.push({ key: "test", version: sku.testVersion, status: sku.testVersionStatus, urls: { mac: sku.testDownloadMacUrl, windows: sku.testDownloadWinUrl } })
  return (
    <>
      {rows.length === 0 && <p className="installer-row library-muted">No installer available yet.</p>}
      {rows.map((row) => (
        <div key={row.key} className="installer-row">
          <DownloadButtons row={row} />
          {/* pre-release builds draw the eye, like the Licenses type badge */}
          {row.status && row.status !== SkuVersionStatus.Released && (
            <span className={`badge-stage${row.status === SkuVersionStatus.Draft ? "" : " badge-stage-attention"}`}>
              {skuVersionStatusLabels[row.status]}
            </span>
          )}
        </div>
      ))}
      {rows.length > 0 && (
        <a
          href={`#versions-${sku.id}`}
          className="card-link installer-all-versions"
          onClick={(e) => { e.preventDefault(); setVersionsOpen(true) }}
        >
          All versions
        </a>
      )}
      {versionsOpen && (
        <AllVersionsDialog
          sku={sku}
          accessToken={accessToken}
          onClose={() => setVersionsOpen(false)}
        />
      )}
    </>
  )
}

// A tile: logo/name, the screenshot in a fixed-height box (aspect-fit, so
// every tile is the same height whatever the image's proportions), then
// the download button(s) and the versions link
function InstallerTile({ sku, hasReleasedEntry, accessToken }: { sku: Sku, hasReleasedEntry: boolean, accessToken: string }) {
  return (
    <div className="license-group installer-tile">
      <h2 className="section-title installer-tile-name">
        <SkuName sku={sku} variant="section" />
      </h2>
      <div className="installer-tile-img">
        <CardImage imageUrl={sku.imageUrl} availableWidths={sku.availableWidths} alt={sku.name} />
      </div>
      <div className="installer-tile-actions">
        <InstallerRows sku={sku} hasReleasedEntry={hasReleasedEntry} accessToken={accessToken} />
      </div>
    </div>
  )
}

function EmptyInstallers() {
  return (
    <div className="empty-state-block">
      <span className="empty-state-icon">&#128229;</span>
      <span>You don't have any installers yet</span>
      <span className="empty-state-hint">Installers for the products you buy or redeem a code for show up here</span>
      <div style={{ display: "flex", gap: 24, marginTop: 12 }}>
        <Link to="/store" className="card-link">Visit Store</Link>
        <Link to="/redemptions" className="card-link">Redeem Code</Link>
      </div>
    </div>
  )
}

// A sku the user can install: the sku, whether an entitlement covers a
// released version (vs. only its test build), and the products the user's
// entitled versions cover. Shared with the Dashboard's Installers card.
export interface InstallerSku {
  sku: Sku
  hasReleasedEntry: boolean
  productIds: Set<string>
}

// One entry per sku with a live entitlement, bundles first (the more
// products a sku's installer covers, the sooner it appears — one download
// replaces several), ties in the order the skus first appear among the
// entries (newest entitlement first, like Licenses); expired entitlements
// drop out the way they do there by default
export function installerSkus(library: Library): InstallerSku[] {
  const skusById = new Map(library.skus.map(sku => [sku.id, sku]))
  const tiles = new Map<string, InstallerSku>()
  for (const entry of library.entries) {
    if (entryExpired(entry)) continue
    const sku = skusById.get(entry.skuId)
    if (!sku) continue
    // the entitlement is for the sku's test version (Licenses' rule) —
    // anything else is a released version
    const isTestEntry = sku.testVersionId != null && entry.skuVersionId === sku.testVersionId
    const tile = tiles.get(sku.id) ?? { sku, hasReleasedEntry: false, productIds: new Set<string>() }
    tile.hasReleasedEntry = tile.hasReleasedEntry || !isTestEntry
    // the products the user's entitled versions cover, across entries
    for (const productId of entry.productIds) tile.productIds.add(productId)
    tiles.set(sku.id, tile)
  }
  // Array.prototype.sort is stable, so equal counts keep the entry order
  return Array.from(tiles.values()).sort((a, b) => b.productIds.size - a.productIds.size)
}

// The one-click download the Dashboard card offers for a sku: the latest
// release when an entitlement covers one, else the user's test build, for
// the visitor's platform (mac when undetected). null = nothing to download.
export function primaryDownload({ sku, hasReleasedEntry }: InstallerSku): { version: string, platform: Platform, url: string } | null {
  const platform: Platform = userPlatform ?? "mac"
  const released = hasReleasedEntry && sku.latestVersion
    ? { version: sku.latestVersion, url: platform === "mac" ? sku.downloadMacUrl : sku.downloadWinUrl }
    : null
  const test = sku.testVersionId && sku.testVersion
    ? { version: sku.testVersion, url: platform === "mac" ? sku.testDownloadMacUrl : sku.testDownloadWinUrl }
    : null
  const pick = released?.url ? released : test?.url ? test : null
  return pick ? { version: pick.version, platform, url: pick.url! } : null
}

export default function Installers() {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: library, status } = useLibrary(accessToken)

  const view = useMemo(() => library ? installerSkus(library) : null, [library])

  const isEmpty = view !== null && view.length === 0

  return (
    <div className="page">
      <h1 className="page-title">Installers</h1>

      {view === null && status !== FetchableStateStatus.Error && <p className="empty-state"><Spinner size={16} /> Loading installers…</p>}
      {view === null && status === FetchableStateStatus.Error && <p className="empty-state">Error loading installers</p>}
      {isEmpty && <EmptyInstallers />}

      {view !== null && view.length > 0 && (
        <div className="installer-tiles">
          {view.map(({ sku, hasReleasedEntry }) => (
            <InstallerTile key={sku.id} sku={sku} hasReleasedEntry={hasReleasedEntry} accessToken={accessToken} />
          ))}
        </div>
      )}
    </div>
  )
}
